"use client";

import useSWRInfinite from "swr/infinite";
import axios from "axios";
import { BackendURLContantas } from "@/constants/URLConstants";
import { useEffect, useMemo } from "react";

export type ContestUserItem = {
  uuid: string;
  name: string | null;
  userId: string | null;
};

export type ContestUsersResponse = {
  data: ContestUserItem[];
  currentPage: number;
  lastPage: number;
};

type Args = {
  q?: string;
  perPage?: number;
  enabled?: boolean;
};

const DEFAULT_PER_PAGE = 20;

export function useContestUsersInfinite({
  q,
  perPage = DEFAULT_PER_PAGE,
  enabled = true,
}: Args) {
  const baseFromConst = BackendURLContantas?.CONTEST?.USERS?.PATH_NAME;

  const base =
    baseFromConst && baseFromConst.length > 0 ? baseFromConst : "/api/contest/users";

  const stableParams = useMemo(() => {
    return {
      q: (q ?? "").trim(),
      per_page: String(perPage),
    };
  }, [q, perPage]);

  const paramsKey = useMemo(() => {
    const sp = new URLSearchParams(stableParams);
    return sp.toString();
  }, [stableParams]);

  const getKey = (index: number, prev: ContestUsersResponse | null) => {
    if (!enabled) return null;
    if (prev && prev.currentPage >= prev.lastPage) return null;

    const params = new URLSearchParams({
      page: String(index + 1),
      ...stableParams,
    });

    return `${base}?${params.toString()}`;
  };

  const fetcher = async (url: string): Promise<ContestUsersResponse> => {
    const { data } = await axios.get(url);
    return data as ContestUsersResponse;
  };

  const swr = useSWRInfinite<ContestUsersResponse>(getKey, fetcher, {
    initialSize: 1,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    dedupingInterval: 300, 
  });

  // 条件が変わったら先頭ページから取り直す
  useEffect(() => {
    if (!enabled) return;
    swr.setSize(1);
  }, [enabled, paramsKey]); 

  const pages = swr.data ?? [];
  const items = pages.flatMap((p) => p.data);

  const last = pages[pages.length - 1];
  const isReachingEnd = last ? last.currentPage >= last.lastPage : false;

  const isLoadingInitial = !swr.data && !swr.error;
  const isLoadingMore = swr.isValidating && swr.size > 1;

  const loadMore = () => {
    if (!swr.isValidating && !isReachingEnd) swr.setSize(swr.size + 1);
  };

  const reset = () => {
    swr.setSize(1);
  };

  return {
    items,
    isLoadingInitial,
    isLoadingMore,
    isReachingEnd,
    loadMore,
    mutate: swr.mutate,
    setSize: swr.setSize,
    reset,
    error: swr.error,
    base,
  };
}